import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, Radio, FileText, LogOut, Menu, X, Music } from "lucide-react";
import { useState } from "react";

// Main Navigation Component - Consistent across all pages
const MainNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
    navigate("/");
  };

  const getDashboardPath = () => {
    if (!user) return "/auth";
    if (user.role === "admin") return "/admin";
    if (user.role === "artist") return "/artist/dashboard";
    return "/reviewer/dashboard";
  };

  const isActive = (path) => {
    if (path === "/live") return location.pathname === "/live";
    if (path === "/offline-reviewers") return location.pathname === "/offline-reviewers";
    // Dashboard active check - includes both dashboard routes
    if (path === "dashboard") {
      return location.pathname === "/artist/dashboard" || 
             location.pathname === "/reviewer/dashboard" ||
             location.pathname === "/admin";
    }
    return false;
  };

  const navLinks = [
    { path: "/live", label: "Live", icon: Radio, indicator: true },
    { path: "/offline-reviewers", label: "Offline", icon: FileText },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-lg border-b border-gray-800/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
        {/* Logo - Always goes to landing page */}
        <div 
          className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity" 
          onClick={() => navigate("/")}
        >
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Music className="w-6 h-6 text-white" />
          </div>
          <span className="text-lg sm:text-xl font-bold text-white">Afterplay</span>
        </div>

        {/* Desktop Navigation - Centered pills */}
        <div className="hidden md:flex items-center gap-1 bg-slate-800/50 rounded-xl p-1">
          {/* Dashboard button first */}
          {user && (
            <Button
              variant="ghost"
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all text-sm ${
                isActive("dashboard")
                  ? "bg-purple-500/30 text-purple-300"
                  : "text-gray-300 hover:text-white hover:bg-slate-700/50"
              }`}
              onClick={() => navigate(getDashboardPath())}
            >
              <LayoutDashboard className="w-4 h-4" />
              Dashboard
            </Button>
          )}
          
          {navLinks.map((link) => (
            <Button
              key={link.path}
              variant="ghost"
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all text-sm ${
                isActive(link.path)
                  ? link.path === "/live" 
                    ? "bg-red-500/30 text-red-300" 
                    : "bg-blue-500/30 text-blue-300"
                  : "text-gray-300 hover:text-white hover:bg-slate-700/50"
              }`}
              onClick={() => navigate(link.path)}
            >
              {link.indicator && (
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              )}
              <link.icon className="w-4 h-4" />
              {link.label}
            </Button>
          ))}
        </div>
        
        {/* Right side - User actions */}
        <div className="hidden md:flex items-center gap-2">
          {user ? (
            <>
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-semibold text-sm">
                {user?.name?.[0]?.toUpperCase() || 'U'}
              </div>
              <span className="text-sm text-gray-300 max-w-[100px] truncate">{user?.display_name || user?.name}</span>
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-white hover:bg-red-500/20"
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <Button
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-sm"
              onClick={() => navigate("/auth")}
            >
              Sign In
            </Button>
          )}
        </div>

        {/* Mobile Menu Button */}
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden text-white"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </Button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-slate-900/95 backdrop-blur-lg border-t border-gray-800/50">
          <div className="px-4 py-3 space-y-1">
            {user && (
              <Button
                variant="ghost"
                className={`w-full justify-start ${
                  isActive("dashboard")
                    ? "text-purple-300 bg-purple-500/20"
                    : "text-gray-300 hover:text-white hover:bg-slate-800"
                }`}
                onClick={() => { navigate(getDashboardPath()); setMobileMenuOpen(false); }}
              >
                <LayoutDashboard className="w-5 h-5 mr-3" />
                Dashboard
              </Button>
            )}
            
            {navLinks.map((link) => (
              <Button
                key={link.path}
                variant="ghost"
                className={`w-full justify-start ${
                  isActive(link.path)
                    ? "text-white bg-purple-500/20"
                    : "text-gray-300 hover:text-white hover:bg-slate-800"
                }`}
                onClick={() => { navigate(link.path); setMobileMenuOpen(false); }}
              >
                <link.icon className="w-5 h-5 mr-3" />
                {link.indicator && (
                  <div className="w-2 h-2 rounded-full bg-green-500 mr-2 animate-pulse" />
                )}
                {link.label}
              </Button>
            ))}
            
            <div className="pt-2 border-t border-gray-800">
              {user ? (
                <Button
                  variant="ghost"
                  className="w-full justify-start text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  onClick={handleLogout}
                >
                  <LogOut className="w-5 h-5 mr-3" />
                  Log Out
                </Button>
              ) : (
                <Button
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                  onClick={() => { navigate("/auth"); setMobileMenuOpen(false); }}
                >
                  Sign In
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default MainNavigation;
